import type { DomainError } from '@obraplan/core/domain/shared/domain-error';

export type AppErrorKind =
  | 'validation'
  | 'forbidden'
  | 'not-found'
  | 'conflict'
  | 'business-rule'
  | 'storage'
  | 'unexpected';

export interface FieldError {
  readonly field: string;
  readonly message: string;
}

/**
 * Falha de uma operação de aplicação.
 *
 * Estende `Error` porque o caminho inesperado precisa de pilha para diagnóstico. Ainda
 * assim, falhas **esperadas** são devolvidas por `Result`, não lançadas — só o
 * `unexpected` sobe até o `ErrorBoundary`.
 *
 * `code` é estável e usado na mensagem ao usuário, no teste e no log.
 * Detalhe interno nunca chega à tela (PLANO_GERAL.md §4.3).
 */
export class AppError extends Error {
  readonly code: string;
  readonly kind: AppErrorKind;
  readonly details: Readonly<Record<string, unknown>> | undefined;
  readonly fieldErrors: readonly FieldError[];

  constructor(
    kind: AppErrorKind,
    code: string,
    message: string,
    options?: {
      details?: Record<string, unknown>;
      fieldErrors?: readonly FieldError[];
      cause?: unknown;
    },
  ) {
    super(message, options?.cause === undefined ? undefined : { cause: options.cause });
    this.name = 'AppError';
    this.kind = kind;
    this.code = code;
    this.details = options?.details;
    this.fieldErrors = options?.fieldErrors ?? [];
  }

  /** Uma falha de regra de domínio é uma falha de regra de negócio da aplicação. */
  static fromDomain(error: DomainError): AppError {
    return new AppError('business-rule', error.code, error.message, {
      ...(error.details === undefined ? {} : { details: { ...error.details } }),
    });
  }

  static isAppError(value: unknown): value is AppError {
    return value instanceof AppError;
  }
}

export function validationError(
  code: string,
  message: string,
  fieldErrors: readonly FieldError[] = [],
): AppError {
  return new AppError('validation', code, message, { fieldErrors });
}

export function forbiddenError(
  code: string,
  message: string,
  details?: Record<string, unknown>,
): AppError {
  return new AppError('forbidden', code, message, details === undefined ? {} : { details });
}

export function notFoundError(
  code: string,
  message: string,
  details?: Record<string, unknown>,
): AppError {
  return new AppError('not-found', code, message, details === undefined ? {} : { details });
}

export function conflictError(
  code: string,
  message: string,
  details?: Record<string, unknown>,
): AppError {
  return new AppError('conflict', code, message, details === undefined ? {} : { details });
}

export function businessRuleError(
  code: string,
  message: string,
  details?: Record<string, unknown>,
): AppError {
  return new AppError('business-rule', code, message, details === undefined ? {} : { details });
}

export function storageError(
  code: string,
  message: string,
  details?: Record<string, unknown>,
): AppError {
  return new AppError('storage', code, message, details === undefined ? {} : { details });
}

export function unexpectedError(message: string, cause?: unknown): AppError {
  return new AppError('unexpected', 'SYSTEM.UNEXPECTED', message, { cause });
}
